import type { Customer } from "@api/types";
import { flattenObject } from "@lib/utils";
import Papa from "papaparse";

const CORE_HEADERS = [
  "First Name",
  "Last Name",
  "Phone Number",
  "Email",
  "Source",
  "Opted Out",
  "Date Added",
] as const;

function formatCustomerCore(customer: Customer): Record<string, string> {
  return {
    "First Name": customer.firstName ?? "",
    "Last Name": customer.lastName ?? "",
    "Phone Number": customer.phoneNumber ?? "",
    Email: customer.email ?? "",
    Source: customer.source ?? "",
    "Opted Out": customer.optout ? "true" : "false",
    "Date Added": customer.createdAt ? new Date(customer.createdAt).toISOString() : "",
  };
}

function getPropertyColumns(customers: Customer[]): string[] {
  const keys = new Set<string>();
  for (const customer of customers) {
    if (!customer.properties) continue;
    const flat = flattenObject(customer.properties as Record<string, string>);
    for (const key of Object.keys(flat)) {
      keys.add(key);
    }
  }
  return Array.from(keys).sort();
}

export function buildContactsExportCsv(customers: Customer[]): string {
  const propertyColumns = getPropertyColumns(customers);
  const headers = [...CORE_HEADERS, ...propertyColumns];

  const rows = customers.map((customer) => {
    const row: Record<string, string> = { ...formatCustomerCore(customer) };
    const flat = customer.properties
      ? flattenObject(customer.properties as Record<string, string>)
      : {};
    for (const col of propertyColumns) {
      row[col] = flat[col] ?? "";
    }
    return row;
  });

  return Papa.unparse(rows, { columns: headers });
}

export function downloadCsvFile(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
